import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ComercioIADetalle, EstadoIA, IaService, RegistroIA } from 'src/app/services/ia/ia.service';
import {
  BADGE_ESTADO, ETIQUETA_ESTADO, numeroONulo, vacio, ETIQUETA_TIPO, etiquetaFila, motivoLegible, errorDeIA, fechaCorta, fechaHora, nf, puedeConfigurarIA, usd, usdCorto,
} from 'src/app/services/ia/ia-formato';

/**
 * «ASISTENTE DE IA» — la solapa de la ficha del comercio,
 * `console/merchants/:code/asistente`.
 *
 * Juan, 2026-09-25: *«voy a poder habilitar o deshabilitar el asistente de IA
 * personalizado por cada comercio, dentro del comercio en kredit-user»*.
 *
 * Aquí vive todo lo de UN comercio: su interruptor (nace apagado), su tope
 * diario propio, su clave propia opcional, lo que gastó hoy y en 7 días, y sus
 * últimas 20 preguntas con la respuesta. «Probar como este comercio» lleva a
 * `console/ia/probar?comercio=<código>`, que es la única entrada al asistente
 * desde la consola.
 *
 * 🔴 Es RUTA HIJA de la ficha (como Tiendas o Condiciones): el encabezado, la
 * tira del portal y las solapas los pone el padre. Por eso aquí no hay miga,
 * ni título, ni `kb-page`.
 *
 * 🔴 El interruptor de aquí es UNO de seis (CONTRATO §2). Prenderlo no basta si
 * la IA está apagada, si el agente está apagado o si no hay clave: la pantalla
 * lo dice, para que nadie crea que el botón ya salió en el portal.
 */
@Component({
  selector: 'app-comercios-asistente',
  templateUrl: './comercios-asistente.component.html',
  styleUrls: ['./comercios-asistente.component.css']
})
export class ComerciosAsistenteComponent implements OnInit, OnDestroy {

  codigo = '';
  cargando = true;
  error: string | null = null;
  ia: ComercioIADetalle | null = null;
  estado: EstadoIA | null = null;
  gasto7d: number | null = null;

  cambiando = false;
  errorInterruptor: string | null = null;

  tope: any = '';
  guardandoTope = false;
  resultadoTope: { ok: boolean; texto: string } | null = null;

  abierta: string | null = null;

  readonly usd = usd;
  readonly usdCorto = usdCorto;
  readonly nf = nf;
  readonly fechaHora = fechaHora;
  readonly fechaCorta = fechaCorta;
  readonly ETIQUETA_ESTADO = ETIQUETA_ESTADO;
  readonly BADGE_ESTADO = BADGE_ESTADO;
  readonly ETIQUETA_TIPO = ETIQUETA_TIPO;
  readonly etiquetaFila = etiquetaFila;
  readonly motivoLegible = motivoLegible;

  private sub?: Subscription;

  constructor(private ruta: ActivatedRoute, private _ia: IaService, public _auth: AuthService) { }

  ngOnInit(): void {
    this.sub = this.ruta.paramMap.subscribe((p) => {
      this.codigo = (p.get('code') || '').toUpperCase();
      this.cargar();
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  get puedeEditar(): boolean { return puedeConfigurarIA(this._auth); }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = null;
    this.abierta = null;
    const [det, est, res] = await Promise.all([
      this._ia.getComercio(this.codigo).then((d) => ({ d, err: null as string | null }))
        .catch((e) => ({ d: null, err: errorDeIA(e) })),
      this._ia.getEstado().catch(() => null),
      this._ia.getResumen(7, this.codigo).catch(() => null),
    ]);
    this.ia = det.d;
    this.error = det.err;
    this.estado = est;
    this.gasto7d = res && typeof res.totalUsd === 'number' ? res.totalUsd : null;
    this.sincronizarTope();
    this.cargando = false;
  };

  private sincronizarTope = (): void => {
    const t = this.ia?.topeDiarioUsd;
    this.tope = t !== null && t !== undefined ? String(t) : '';
  };

  get ultimas(): RegistroIA[] { return (this.ia?.ultimas || []).slice(0, 20); }

  /**
   * Lo que hoy impide que el botón aparezca aunque este comercio esté prendido,
   * de afuera hacia adentro (CONTRATO §2). Vacío = nada lo impide, o no se sabe.
   */
  get impedimentos(): string[] {
    const e = this.estado;
    if (!e) return [];
    const r: string[] = [];
    if (e.apagado?.apagado) r.push('la IA está apagada para todos');
    const hayClave = Object.values(e.proveedores || {}).some((p: any) => p && p.configurado);
    if (!hayClave && !this.ia?.credencialPropia) r.push('no hay ninguna clave de proveedor cargada');
    const ag = e.agentes?.['asistente-comercio'];
    if (ag && ag.habilitado === false) r.push('el agente «Asistente del comercio» está apagado');
    if (Number(e.topeDiarioUsd) > 0 && Number(e.gastoHoyUsd) >= Number(e.topeDiarioUsd)) r.push('hoy se llegó al tope diario de plataforma');
    const tp = this.ia?.topeDiarioUsd;
    if (tp !== null && tp !== undefined && Number(this.ia?.gastoHoyUsd || 0) >= Number(tp)) r.push('hoy se llegó al tope propio de este comercio');
    return r;
  }

  alternar = async (habilitado: boolean): Promise<void> => {
    if (!this.ia || this.cambiando) return;
    this.cambiando = true;
    this.errorInterruptor = null;
    try {
      await this._ia.guardarComercio(this.codigo, { habilitado });
      this.ia.habilitado = habilitado;
    } catch (e: any) {
      this.errorInterruptor = errorDeIA(e, true);
    } finally {
      this.cambiando = false;
    }
  };

  get topeValido(): boolean {
    if (vacio(this.tope)) return true;   // vacío = sin tope propio
    const n = Number(this.tope);
    return !isNaN(n) && n >= 0;
  }

  get topeCambiado(): boolean {
    const actual = this.ia?.topeDiarioUsd;
    const nuevo = numeroONulo(this.tope);
    return (vacio(actual) ? null : Number(actual)) !== nuevo;
  }

  guardarTope = async (quitar = false): Promise<void> => {
    if (!this.ia || this.guardandoTope) return;
    if (quitar) this.tope = '';
    if (!this.topeValido) return;
    const n = numeroONulo(this.tope);
    const valor = n === null ? null : Math.round(n * 100) / 100;
    this.guardandoTope = true;
    this.resultadoTope = null;
    try {
      await this._ia.guardarComercio(this.codigo, { topeDiarioUsd: valor });
      this.ia.topeDiarioUsd = valor;
      this.sincronizarTope();
      this.resultadoTope = { ok: true, texto: valor === null
        ? 'Sin tope propio: manda el de plataforma.' : `Guardado: ${usd(valor)} por día.` };
    } catch (e: any) {
      this.resultadoTope = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      this.guardandoTope = false;
    }
  };

  releerEstado = async (): Promise<void> => {
    try { this.estado = await this._ia.getEstado(); } catch { /* queda el anterior */ }
    try { this.ia = await this._ia.getComercio(this.codigo); } catch { /* queda el anterior */ }
  };

  idDe = (r: RegistroIA, i: number): string => r._id || r.traceId || String(i);

  alternarFila = (r: RegistroIA, i: number): void => {
    const id = this.idDe(r, i);
    this.abierta = this.abierta === id ? null : id;
  };
}
